import { useQuery } from '@tanstack/react-query';
import { ReviewListContainer } from '~/components/containers/ReviewListContainer/ReviewListContainer';
import foodsService from '~/services/foodsService/foods.service';
import { useLocale } from '~/hooks/useLocale';
import { ScrollShadow, Button } from "@heroui/react";
import { ROUTES } from '~/constants/routes.contants';
import { Breadcrumbs } from '~/components/molecules/Breadcrumbs/Breadcrumbs';
import { useTranslation } from 'react-i18next';
import { UserProfileCard } from '~/components/molecules/UserProfileCard/UserProfileCard';
import { PlaceCardSmall } from '~/components/molecules/PlaceCardSmall/PlaceCardSmall';
import { BestBadge } from '~/components/atoms/BestBadge';
import { FoodCard } from '~/components/molecules/FoodCard/FoodCard';
import { useState } from 'react';
import SEO from '~/components/molecules/SEO/SEO';
import ReviewModal from '~/components/molecules/ReviewModal';
import { useAuthModal } from '~/hooks/useAuthModal';
import { FaPen } from 'react-icons/fa';
import { getIdFromSlug } from '~/utils/slug-utils';
import { GetFoodByIdResponseDTO } from '@bitecritic-api/src/foods/dto/food.dto';
import { GetServerSidePropsContext } from 'next';
import { getCurrentLocaleByCode } from '~/i18n';
import { FoodResponseDto } from '@bitecritic-api/src/foods/dto/food.response.dto';
import { sanitize } from '~/utils/client-utils';
import { useEvent } from '~/hooks/useEvent';
import { EndOfScroll } from '~/components/molecules/EndOfScroll';
import { BeTheFirstReviewer } from '~/components/molecules/BeTheFirstReviewer';

// Type adapter function to convert GetFoodByIdResponseDTO to FoodResponseDto
const adaptFoodResponse = (food: GetFoodByIdResponseDTO): FoodResponseDto => {
  return {
    ...food,
    category: food.category ? {
      ...food.category,
      // Ensure mainImageId is never null for the FoodResponseDto
      mainImageId: food.category.mainImageId || '/images/empty.webp'
    } : undefined
  };
};

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { locale, query } = context;
  const parsedId = getIdFromSlug(query.id as string);
  // try catch if fail 404
  try {
    const foodData = await foodsService.getFoodById(Number(parsedId), getCurrentLocaleByCode(locale as string).language);
    return { props: { foodData, id: Number(parsedId) } }
  } catch (error) {
    return { notFound: true }
  }
}

export default function FoodDetailPage({ foodData, id }: { foodData: GetFoodByIdResponseDTO, id: number }) {
  const { currentLocale } = useLocale();
  const { t } = useTranslation();
  const { requireAuth } = useAuthModal();
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const { sendEvent } = useEvent();
  const { data: food, isLoading, error } = useQuery<GetFoodByIdResponseDTO | null>({
    queryKey: ['food', id],
    initialData: foodData,
    queryFn: () => foodsService.getFoodById(Number(id), currentLocale.language),
    enabled: !!id,
  });

  const handleAddReview = () => {
    sendEvent('add_review_button_click', {
      foodId: food?.id,
      source: food?.reviewCount === 0 ? 'be_the_first_cta' : 'standard_button',
    });
    requireAuth(() => setIsReviewModalOpen(true));
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-4 bg-white">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-content2 rounded-lg w-1/4" />
          <div className="h-4 bg-content2 rounded-lg w-1/2" />
          <div className="h-64 bg-content2 rounded-lg" />
        </div>
      </div>
    );
  }

  if (error || !food) {
    return (
      <div className="container mx-auto p-4 bg-white">
        <div className="text-center text-danger-600">
          {t('common.error.default')}
        </div>
      </div>
    );
  }

  // Adapt the food data to ensure type compatibility
  const adaptedFood = adaptFoodResponse(food);

  // Generate food-specific keywords with translations
  const translatedKeywords = [
    t('seo.keywords.food'),
    t('seo.keywords.cuisine'),
    t('seo.keywords.reviews'),
    t('seo.keywords.rating'),
    t('seo.keywords.restaurants'),
    t('seo.keywords.dining')
  ];

  // Combine translated keywords with food-specific data
  const foodKeywords = [
    food.name,
    food.category?.name,
    ...translatedKeywords,
    ...(Array.isArray(food.tags) ? food.tags.map(tag => tag.name) : []),
  ].filter(Boolean).join(', ');

  // Generate SEO title with translations - follows 55-60 char limit
  const seoTitle = t('seo.food.title', {
    foodName: food.name,
    defaultValue: `${food.name} Review`
  });

  // Generate SEO description with translations - focused on "best places to eat"
  const seoDescription = t('seo.food.description', {
    foodName: food.name,
    rating: food.reviewOverallRating ? food.reviewOverallRating.toFixed(1) : '0.0',
    category: food.category?.name || t('common.food'),
    defaultValue: `Discover the best places to eat ${food.name}. Read honest reviews, ratings, and find where to enjoy the most delicious ${food.name} near you on BiteCritic.`
  });

  return (
    <div className="space-y-4 bg-white">
      <SEO
        title={seoTitle}
        description={seoDescription}
        type="product"
        image={food.mainImageId || '/images/hero-bg.webp'}
        keywords={foodKeywords}
        productData={{
          name: food.name,
          image: food.mainImageId || '/images/hero-bg.webp',
          description: food.description ?
            sanitize(food.description)?.substring(0, 500) || seoDescription :
            seoDescription,
          brand: food.category?.name,
          aggregateRating: {
            ratingValue: food.reviewOverallRating || 0,
            reviewCount: food.reviewCount || 0,
            bestRating: 10,
            worstRating: 0.01
          }
        }}
      />

      <div className="container mx-auto px-4">
        <Breadcrumbs items={[
          { label: t('foods.title'), href: ROUTES.FOODS },
          { label: food.category?.name || '', href: `${ROUTES.CATEGORIES}/${food.category?.id}` },
          { label: food.name || '', href: undefined }
        ]}
          classNames={{
            link: "text-primary-600 hover:text-primary-700",
            separator: "text-foreground/60"
          }}
        />

        {/* Food Card */}
        <div className="flex flex-col gap-4 mt-4 mx-auto w-full md:w-2/3 lg:w-1/2">
          <FoodCard isDetailPage={true} data={adaptedFood} />
          {food.reviewCount > 0 && (
            <Button
              size="lg"
              color="primary"
              onPress={handleAddReview}
              startContent={<FaPen />}
              className='text-wrap shadow-md hover:shadow-lg transition-shadow'
            >
              {t('add-review-to-food', { food: food.name })}
            </Button>
          )}
        </div>

        {/* description */}
        {food.description && (
          <div className="container mx-auto py-4">
            <p className={`${isDescriptionExpanded ? '' : 'line-clamp-3'} overflow-hidden text-foreground/80`}>{sanitize(food.description)}</p>
            <div className="flex justify-end">
              <Button
                className="mt-2"
                variant="flat"
                color="secondary"
                size="sm"
                onPress={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
              >
                {isDescriptionExpanded ? t('common.showLess') : t('common.showMore')}
              </Button>
            </div>
          </div>
        )}

        {/* Top Places Section */}
        {food.topPlaces && food.topPlaces.length > 0 && (
          <div className="py-6 px-4 bg-foreground-800 rounded-lg my-6 shadow-md">
            <div className="mb-6">
              <h2 className="text-2xl font-bold mb-2 text-warning-300">{t('food.bestPlaces', { food: food.name })}</h2>
              <p className="text-small text-white">{t('food.bestPlacesSectionDescription', { food: food.name })}</p>
            </div>
            <ScrollShadow orientation="horizontal" className="w-full">
              <div className="flex gap-4 pb-4" style={{ minWidth: 'min-content' }}>
                {food.topPlaces.map((topPlace, index) => (
                  <div key={topPlace.id} className="min-w-[300px] relative">
                    <BestBadge rank={index + 1} />
                    <PlaceCardSmall
                      place={{
                        ...topPlace,
                        reviewOverallRating: topPlace.reviewOverallRating || 0,
                        totalReviews: topPlace.totalReviews || 0,
                      }}
                    />
                  </div>
                ))}
              </div>
            </ScrollShadow>
          </div>
        )}

        {/* Top Reviewers Section */}
        {food.topReviewers && food.topReviewers.length > 0 && (
          <div className="py-6 px-4 bg-secondary-100 rounded-lg my-6 shadow-md">
            <div className="mb-6">
              <h2 className="text-2xl font-bold mb-2 text-secondary-600">{t('food.gourmets', { food: food.name })}</h2>
              <p className="text-small text-foreground/70">{t('food.gourmetsSectionDescription', { food: food.name })}</p>
            </div>
            <ScrollShadow orientation="horizontal" className="w-full">
              <div className="flex gap-4 pb-4" style={{ minWidth: 'min-content' }}>
                {food.topReviewers.map((reviewer, index) => (
                  <div key={reviewer.id} className="min-w-[300px] relative">
                    <BestBadge rank={index + 1} />
                    <UserProfileCard
                      user={{
                        ...reviewer
                      }}
                      showStats={false}
                      stats={{
                        reviewCount: reviewer.reviewCount || 0,
                        averageRating: reviewer.averageRating || 0,
                      }}
                    />
                  </div>
                ))}
              </div>
            </ScrollShadow>
          </div>
        )}

        {/* Reviews Section - Conditional Rendering */}
        <div className='py-6'>
          {food.reviewCount === 0 ? (
            <BeTheFirstReviewer
              foodName={food.name}
              onAddReview={handleAddReview}
            />
          ) : (
            <>
              <ReviewListContainer foodId={Number(id)} />
              <div className='w-full flex justify-center mt-8'>
                <Button
                  size="lg"
                  color="warning"
                  onPress={handleAddReview}
                  startContent={<FaPen />}
                  className='shadow-md hover:shadow-warning-500/30 transition-all text-wrap'
                >
                  {t('add-review-to-food', { food: food.name })}
                </Button>
              </div>
            </>
          )}
        </div>

        <ReviewModal
          isOpen={isReviewModalOpen}
          onClose={() => setIsReviewModalOpen(false)}
          initialFood={adaptedFood}
        />
      </div>
      <EndOfScroll />
    </div>
  );
} 